import { z } from "zod";

/**
 * Wire types + schemas for every backend endpoint the marketing site
 * speaks to. The `*Schema` exports double as runtime validators inside
 * the form primitive, so a single source of truth covers TS types and
 * client-side validation.
 *
 * Keep these tight. Anything optional should be documented inline.
 */

const emailSchema = z
  .string()
  .min(1, "Please enter an email")
  .email("That doesn't look like a valid email");

const nameSchema = z
  .string()
  .trim()
  .min(2, "Tell us your name")
  .max(80, "Keep it under 80 characters");

const longTextSchema = z
  .string()
  .trim()
  .min(10, "A few more words please")
  .max(2000, "Keep it under 2000 characters");

const optionalLongTextSchema = z.string().trim().max(2000).optional();

const optionalPhoneSchema = z.string().trim().max(40, "Keep it under 40 characters").optional();

import { SUPPORTED_LOCALES } from "@/lib/i18n/config";

const localeSchema = z.enum(SUPPORTED_LOCALES);

const touchSnapshotSchema = z
  .object({
    utm_source: z.string().max(120).optional(),
    utm_medium: z.string().max(120).optional(),
    utm_campaign: z.string().max(120).optional(),
    utm_content: z.string().max(120).optional(),
    utm_term: z.string().max(120).optional(),
    landingPath: z.string().max(200).optional(),
    landingUrl: z.string().max(500).optional(),
    referrer: z.string().max(500).optional(),
    capturedAt: z.string().max(40).optional(),
  })
  .optional();

const attributionSchema = z
  .object({
    utm_source: z.string().max(120).optional(),
    utm_medium: z.string().max(120).optional(),
    utm_campaign: z.string().max(120).optional(),
    utm_content: z.string().max(120).optional(),
    utm_term: z.string().max(120).optional(),
    gclid: z.string().max(200).optional(),
    fbclid: z.string().max(200).optional(),
    ttclid: z.string().max(200).optional(),
    msclkid: z.string().max(200).optional(),
    entryPoint: z.string().max(40).optional(),
    landingPath: z.string().max(200).optional(),
    landingUrl: z.string().max(500).optional(),
    referrer: z.string().max(500).optional(),
    capturedAt: z.string().max(40).optional(),
    gaClientId: z.string().max(80).optional(),
    firstTouch: touchSnapshotSchema,
    lastTouch: touchSnapshotSchema,
    landingVariant: z.enum(["waitlist", "beta", "launch"]).optional(),
    timeToSubmitMs: z.number().int().nonnegative().max(604_800_000).optional(),
    storePlatform: z.enum(["ios", "android"]).optional(),
    country: z.string().max(8).optional(),
  })
  .optional();

const entryPointSchema = z.string().trim().max(40).optional();

const consentSchema = z.preprocess(
  (value) => value === true || value === "true" || value === "on",
  z
    .boolean()
    .refine((value) => value, {
      message:
        "Please accept the privacy policy so we can respond to your inquiry.",
    }),
);

export const formConsentRecordSchema = z.object({
  accepted: z.literal(true),
  consentText: z.string().min(1),
  policyVersion: z.string().min(1),
  privacyPolicyUrl: z.string().url(),
  termsUrl: z.string().url(),
  locale: z.string().min(2),
  pageUrl: z.string().url(),
  referrer: z.string().url().nullable(),
  userAgent: z.string().min(1),
  acceptedAtClient: z.string().min(1),
  formSource: z.string().min(1),
});

export type FormConsentRecord = z.infer<typeof formConsentRecordSchema>;
/** @deprecated Prefer FormConsentRecord */
export type ContactConsentRecord = FormConsentRecord;
export const contactConsentRecordSchema = formConsentRecordSchema;

/* ─── Waitlist (beta users) ─────────────────────────────────────── */

export const waitlistSubmissionSchema = z.object({
  email: emailSchema,
  name: nameSchema.optional(),
  source: z.string().max(80).optional(),
  locale: localeSchema.optional(),
  entryPoint: entryPointSchema,
  attribution: attributionSchema,
});

export const waitlistInputSchema = waitlistSubmissionSchema.extend({
  consentGiven: consentSchema,
});
export type WaitlistInput = z.infer<typeof waitlistInputSchema>;
export type WaitlistSubmission = z.infer<typeof waitlistSubmissionSchema>;
export const waitlistConfirmationStatusSchema = z.enum([
  "pending",
  "confirmed",
  "already_confirmed",
]);
export type WaitlistConfirmationStatus = z.infer<
  typeof waitlistConfirmationStatusSchema
>;
export type WaitlistResponse = {
  ok: true;
  position?: number;
  confirmationRequired?: boolean;
  confirmationStatus?: WaitlistConfirmationStatus;
  confirmationEmailSent?: boolean;
  confirmationEmailProvider?: "resend" | "brevo";
  /** True when email was already pending — idempotent re-submit, not an error. */
  alreadyRegistered?: boolean;
};

export const waitlistConfirmInputSchema = z.object({
  token: z.string().trim().min(12, "Invalid confirmation token"),
});
export type WaitlistConfirmInput = z.infer<typeof waitlistConfirmInputSchema>;
export type WaitlistConfirmResponse = {
  ok: true;
  status: Extract<WaitlistConfirmationStatus, "confirmed" | "already_confirmed">;
};

/* ─── Investor deck request ─────────────────────────────────────── */

export const investorDeckSubmissionSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  fund: z.string().trim().max(120).optional(),
  message: longTextSchema.optional(),
  locale: localeSchema.optional(),
  entryPoint: entryPointSchema,
  attribution: attributionSchema,
});

export const investorDeckInputSchema = investorDeckSubmissionSchema.extend({
  consentGiven: consentSchema,
});
export type InvestorDeckInput = z.infer<typeof investorDeckInputSchema>;
export type InvestorDeckSubmission = z.infer<typeof investorDeckSubmissionSchema>;
export type InvestorDeckResponse = {
  ok: true;
  deckUrl?: string;
};

/* ─── Collaborator inquiry (stylists, photographers, retailers) ─ */

export const collaboratorLaneSchema = z.enum([
  "stylist",
  "creator",
  "retailer",
  "other",
]);
export type CollaboratorLane = z.infer<typeof collaboratorLaneSchema>;

export const collaboratorSubmissionSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  lane: collaboratorLaneSchema,
  message: longTextSchema,
  locale: localeSchema.optional(),
  entryPoint: entryPointSchema,
  attribution: attributionSchema,
});

export const collaboratorInputSchema = collaboratorSubmissionSchema.extend({
  consentGiven: consentSchema,
});
export type CollaboratorInput = z.infer<typeof collaboratorInputSchema>;
export type CollaboratorSubmission = z.infer<typeof collaboratorSubmissionSchema>;
export type CollaboratorResponse = {
  ok: true;
  ticketId?: string;
};

/* ─── Newsletter (footer) ───────────────────────────────────────── */

export const newsletterSubmissionSchema = z.object({
  email: emailSchema,
  locale: localeSchema.optional(),
  entryPoint: entryPointSchema,
  attribution: attributionSchema,
});

export const newsletterInputSchema = newsletterSubmissionSchema.extend({
  consentGiven: consentSchema,
});
export type NewsletterInput = z.infer<typeof newsletterInputSchema>;
export type NewsletterSubmission = z.infer<typeof newsletterSubmissionSchema>;
export type NewsletterResponse = {
  ok: true;
};

/* ─── Contact (footer / collaborator CTA modal) ─────────────────── */

export const contactSubmissionSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: optionalPhoneSchema,
  message: optionalLongTextSchema,
});

export const contactInputSchema = contactSubmissionSchema.extend({
  consentGiven: consentSchema,
});

export const contactRequestSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: optionalPhoneSchema,
  message: optionalLongTextSchema,
  consentRecord: formConsentRecordSchema,
  entryPoint: entryPointSchema,
  attribution: attributionSchema,
  source: z.string().max(80).optional(),
});

export type ContactInput = z.infer<typeof contactInputSchema>;
export type ContactSubmission = z.infer<typeof contactSubmissionSchema>;
export type ContactRequest = z.infer<typeof contactRequestSchema>;
export type ContactResponse = {
  ok: true;
  ticketId?: string;
};

/* ─── Partner programme application ────────────────────────────── */

export const partnerTypeSchema = z.enum([
  "professional",
  "business",
  "other",
]);
export type PartnerType = z.infer<typeof partnerTypeSchema>;

export const partnerSubmissionSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  company: z
    .string()
    .trim()
    .min(1, "Please enter your company name")
    .max(120, "Keep it under 120 characters"),
  partnerType: partnerTypeSchema,
  message: longTextSchema,
  locale: localeSchema.optional(),
  entryPoint: entryPointSchema,
  attribution: attributionSchema,
});

export const partnerInputSchema = partnerSubmissionSchema.extend({
  consentGiven: consentSchema,
});
export type PartnerInput = z.infer<typeof partnerInputSchema>;
export type PartnerSubmission = z.infer<typeof partnerSubmissionSchema>;
export type PartnerResponse = {
  ok: true;
  ticketId?: string;
};

/* ─── Unified error envelope ────────────────────────────────────── */

export type ApiErrorShape = {
  status: number;
  code: string;
  message: string;
  details?: unknown;
};
